import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { gtmNewsletterSubmit } from '../../utils/gtm';

const EMAIL_KEY = 'finmaple_rrsp_email';
const DISMISS_KEY = 'finmaple_hellobar_dismissed';

const HelloBar = () => {
  const { t } = useTranslation('nav');
  const lp = useLocalizedPath();

  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY) === 'true') setDismissed(true);
    if (localStorage.getItem(EMAIL_KEY)) setSubmitted(true);
  }, []);

  if (dismissed || submitted) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    localStorage.setItem(EMAIL_KEY, email.trim());
    fetch('https://buttondown.com/api/emails/embed-subscribe/Finmaple', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ email: email.trim() }),
    }).catch(() => {});
    if (window.plausible) window.plausible('Newsletter Submit', { props: { source: 'hellobar' } });
    gtmNewsletterSubmit({ source: 'hellobar' });
    setShowChecklist(true);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (showChecklist) {
      setSubmitted(true);
      sessionStorage.setItem(DISMISS_KEY, 'true');
      setDismissed(true);
    }
  };

  const checklistSteps = [
    t('helloBar.step1'),
    t('helloBar.step2'),
    t('helloBar.step3'),
    t('helloBar.step4'),
    t('helloBar.step5'),
  ];

  const benefits = [
    t('helloBar.benefit1'),
    t('helloBar.benefit2'),
    t('helloBar.benefit3'),
  ];

  return (
    <>
      {/* ─── HelloBar top banner ─── */}
      <Box
        sx={{
          bgcolor: '#1E3A5F',
          color: '#fff',
          py: { xs: 0.75, md: 1 },
          px: { xs: 2, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1, md: 2 },
          flexWrap: 'wrap',
          position: 'relative',
          zIndex: 1201,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            fontSize: { xs: '0.75rem', md: '0.85rem' },
            letterSpacing: '0.01em',
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            {t('helloBar.taxSeason')}
          </Box>
          <Box component="span" sx={{ mx: 0.75, display: { xs: 'none', md: 'inline' } }}>|</Box>
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
            {t('helloBar.rrspDeadline')}
          </Box>
          <Box component="span" sx={{ mx: 0.75, display: { xs: 'none', md: 'inline' } }}>|</Box>
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
            {t('helloBar.craDeadline')}
          </Box>
          <Box component="span" sx={{ mx: 0.75, display: { xs: 'none', md: 'inline' } }}>|</Box>
          <Box
            component={RouterLink}
            to={lp('getStarted')}
            sx={{
              color: '#FFEBEE',
              fontWeight: 600,
              textDecoration: 'underline',
              display: { xs: 'none', md: 'inline' },
              '&:hover': { color: '#fff' },
            }}
          >
            {t('helloBar.viewGuide')}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' }, fontWeight: 400 }}>
            {t('helloBar.mobileRrsp')}
          </Box>
        </Typography>

        <Button
          onClick={() => setModalOpen(true)}
          variant="contained"
          size="small"
          sx={{
            bgcolor: '#F9A825',
            color: '#1A1A1A',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '20px',
            px: { xs: 1.5, md: 2 },
            py: 0.4,
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            whiteSpace: 'nowrap',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#FFD54F', boxShadow: 'none' },
          }}
        >
          {t('helloBar.rrspChecklist')}
        </Button>

        <IconButton
          onClick={handleDismiss}
          aria-label={t('closeBanner')}
          size="small"
          sx={{
            color: 'rgba(255,255,255,0.7)',
            position: 'absolute',
            right: 8,
            p: 0.5,
            '&:hover': { color: '#fff' },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ─── Lead Magnet Modal ─── */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          },
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handleCloseModal}
          aria-label={t('helloBar.close')}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            zIndex: 1,
            color: '#5F6B7A',
            bgcolor: 'rgba(0,0,0,0.04)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Box sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 } }}>
          {!showChecklist ? (
            /* ─── Pre-submission ─── */
            <Box component="form" onSubmit={handleSubmit} sx={{ textAlign: 'center' }}>
              {/* Hero icon */}
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #1E3A5F 0%, #2C5282 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <AssignmentTurnedInOutlinedIcon sx={{ color: '#fff', fontSize: 28 }} />
              </Box>

              {/* Welcome badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  bgcolor: '#E8F5E9',
                  color: '#2E7D32',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                {t('helloBar.deadlineBadge')}
              </Box>

              {/* Title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 0.75,
                  fontSize: { xs: '1.25rem', sm: '1.4rem' },
                  lineHeight: 1.3,
                }}
              >
                {t('helloBar.modalTitle')}
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="body2"
                sx={{ color: '#5F6B7A', mb: 3, lineHeight: 1.5 }}
              >
                {t('helloBar.modalSubtitle')}
              </Typography>

              {/* Benefits */}
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                {benefits.map((benefit, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleOutlineIcon
                      sx={{ color: '#2E7D32', fontSize: 20, flexShrink: 0 }}
                    />
                    <Typography variant="body2" sx={{ color: '#1A1A1A', fontWeight: 500 }}>
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Email input */}
              <TextField
                id="hellobar-email"
                name="email"
                type="email"
                required
                fullWidth
                placeholder={t('helloBar.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#F7F9FC',
                    '& fieldset': { borderColor: '#EEF0F3' },
                    '&:hover fieldset': { borderColor: '#1E3A5F' },
                    '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
                  },
                }}
              />

              {/* CTA Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #F9A825 0%, #F57F17 100%)',
                  color: '#1A1A1A',
                  borderRadius: '12px',
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  boxShadow: '0 4px 14px rgba(249,168,37,0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFD54F 0%, #F9A825 100%)',
                    boxShadow: '0 6px 20px rgba(249,168,37,0.5)',
                  },
                }}
              >
                {t('helloBar.submitChecklist')}
              </Button>

              {/* Trust line */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  mt: 2,
                  mb: 1,
                }}
              >
                <LockOutlinedIcon sx={{ color: '#9CA3AF', fontSize: 14 }} />
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  {t('helloBar.trustLine')}
                </Typography>
              </Box>

              {/* Social proof */}
              <Typography variant="caption" sx={{ color: '#5F6B7A', fontWeight: 500 }}>
                {t('helloBar.socialProof')}
              </Typography>
            </Box>
          ) : (
            /* ─── Post-submission ─── */
            <Box sx={{ textAlign: 'center' }}>
              {/* Success icon */}
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#E8F5E9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <TaskAltIcon sx={{ color: '#2E7D32', fontSize: 32 }} />
              </Box>

              {/* Success title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 0.5,
                  fontSize: { xs: '1.25rem', sm: '1.4rem' },
                }}
              >
                {t('helloBar.successTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 3 }}>
                {t('helloBar.successSubtitle')}
              </Typography>

              {/* Checklist steps card */}
              <Box
                sx={{
                  bgcolor: '#F7F9FC',
                  borderRadius: '14px',
                  border: '1px solid #EEF0F3',
                  overflow: 'hidden',
                  textAlign: 'left',
                  mb: 3,
                }}
              >
                {checklistSteps.map((step, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start',
                      px: 2.5,
                      py: 2,
                      borderBottom:
                        i < checklistSteps.length - 1 ? '1px solid #EEF0F3' : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '8px',
                        bgcolor: '#1E3A5F',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 13,
                        flexShrink: 0,
                        mt: 0.1,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#1A1A1A', lineHeight: 1.6, fontWeight: 500 }}
                    >
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* PDF download button */}
              <Button
                component="a"
                href="/downloads/checklist-financiero-90-dias.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (window.plausible) window.plausible('PDF Download', { props: { document: 'checklist-90-dias' } });
                }}
                fullWidth
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #F9A825 0%, #F57F17 100%)',
                  color: '#1A1A1A',
                  borderRadius: '12px',
                  py: 1.5,
                  mb: 2.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  boxShadow: '0 4px 14px rgba(249,168,37,0.4)',
                  textDecoration: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFD54F 0%, #F9A825 100%)',
                    boxShadow: '0 6px 20px rgba(249,168,37,0.5)',
                  },
                }}
              >
                {t('helloBar.downloadPdf', 'Descargar Checklist en PDF')}
              </Button>

              {/* Contact email */}
              <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 2.5 }}>
                {`${t('helloBar.contactLine')} `}
                <Box
                  component="a"
                  href="mailto:hola@finmaple.com"
                  sx={{
                    color: '#1E3A5F',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  hola@finmaple.com
                </Box>
              </Typography>

              {/* Close button */}
              <Button
                fullWidth
                onClick={handleCloseModal}
                variant="outlined"
                sx={{
                  color: '#1E3A5F',
                  borderColor: '#EEF0F3',
                  borderRadius: '12px',
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#1E3A5F',
                    bgcolor: 'rgba(30,58,95,0.04)',
                  },
                }}
              >
                {t('helloBar.close')}
              </Button>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default HelloBar;
